import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../../components/Common/TableContainer";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  UncontrolledTooltip,
  Input,
  Form,
  Button,
  Badge,
  Nav,
  NavItem,
  NavLink,
  FormGroup,
} from "reactstrap";
import classnames from "classnames";
import * as Yup from "yup";
import { useFormik } from "formik";

import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";
import axios from "axios";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { ADMIN_HEADERS, ODATA_BASE } from "../../../config";

// --- API YAPILANDIRMASI ---
const API_BASE = ODATA_BASE;

const PKG_URLS = {
  list: `${API_BASE}/Premium_Package`,
  add: `${API_BASE}/Premium_Package`,
  update: (id) => `${API_BASE}/Premium_Package(${id})`,
  delete: (id) => `${API_BASE}/Premium_Package(${id})`,
};

const SUB_URLS = {
  // İlişkili kullanıcı + paket bilgisini de getir
  list: `${API_BASE}/User_Subscription?$expand=Users,Premium_Package&$orderby=ID desc`,
  add: `${API_BASE}/User_Subscription`,
  update: (id) => `${API_BASE}/User_Subscription(${id})`,
  delete: (id) => `${API_BASE}/User_Subscription(${id})`,
};

const USERS_URL = `${API_BASE}/Users?$select=ID,Users_NameSurname,EMail&$orderby=Users_NameSurname`;

const showToast = (title, message, isSuccess) => {
  toastr.options = { positionClass: "toast-top-right", timeOut: 3000 };
  if (isSuccess) toastr.success(message, title);
  else toastr.error(message, title);
};

const fmtDate = (d) => {
  if (!d) return "-";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("tr-TR");
};

// ISO -> input[type=date] değeri (YYYY-MM-DD)
const toInputDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const PremiumManager = (props) => {
  document.title = "Premium & Abonelik Yönetimi | DLApro";

  const [activeTab, setActiveTab] = useState("packages");

  // Veri
  const [packageList, setPackageList] = useState([]);
  const [subList, setSubList] = useState([]);
  const [usersList, setUsersList] = useState([]);

  // Paket modal
  const [pkgModal, setPkgModal] = useState(false);
  const [pkgIsEdit, setPkgIsEdit] = useState(false);
  const [pkgEditData, setPkgEditData] = useState(null);

  // Abonelik modal
  const [subModal, setSubModal] = useState(false);
  const [subIsEdit, setSubIsEdit] = useState(false);
  const [subEditData, setSubEditData] = useState(null);

  // Silme
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(null); // "pkg" | "sub"
  const [dataToDelete, setDataToDelete] = useState(null);

  useEffect(() => {
    fetchPackages();
    fetchSubscriptions();
    fetchUsers();
  }, []);

  // ==================== VERİ ÇEKME ====================
  const fetchPackages = async () => {
    try {
      const res = await axios.get(PKG_URLS.list);
      setPackageList(res.data.value || []);
    } catch (e) {
      console.error("Paketler çekilemedi:", e);
      showToast("Hata", "Paketler çekilemedi", false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const res = await axios.get(SUB_URLS.list);
      setSubList(res.data.value || []);
    } catch (e) {
      console.error("Abonelikler çekilemedi:", e);
      showToast("Hata", "Abonelikler çekilemedi", false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(USERS_URL, ADMIN_HEADERS);
      setUsersList(res.data.value || []);
    } catch (e) {
      console.error("Kullanıcılar çekilemedi:", e);
    }
  };

  // ==================== PAKET FORM ====================
  const pkgValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      ID: (pkgEditData && pkgEditData.ID) || 0,
      Title: (pkgEditData && pkgEditData.Title) || "",
      Price: (pkgEditData && pkgEditData.Price) || 0,
      Duration_Months: (pkgEditData && pkgEditData.Duration_Months) || 1,
      IsActive:
        pkgEditData && typeof pkgEditData.IsActive === "boolean"
          ? pkgEditData.IsActive
          : true,
    },
    validationSchema: Yup.object({
      Title: Yup.string().required("Paket adı zorunludur."),
      Price: Yup.number().typeError("Fiyat sayı olmalı.").min(0, "Fiyat negatif olamaz."),
      Duration_Months: Yup.number()
        .typeError("Süre sayı olmalı.")
        .min(1, "Süre en az 1 ay olmalı."),
    }),
    onSubmit: (values) => savePackage(values),
  });

  const savePackage = async (values) => {
    const payload = {
      Title: values.Title,
      Price: Number(values.Price) || 0,
      Duration_Months: parseInt(values.Duration_Months) || 1,
      IsActive: !!values.IsActive,
    };
    try {
      if (pkgIsEdit) {
        await axios.patch(PKG_URLS.update(values.ID), payload);
        showToast("Başarılı", "Paket güncellendi.", true);
      } else {
        await axios.post(PKG_URLS.add, payload);
        showToast("Başarılı", "Paket eklendi.", true);
      }
      togglePkg();
      fetchPackages();
    } catch (e) {
      console.error("Paket kaydetme hatası:", e);
      showToast("Hata", "İşlem başarısız.", false);
    }
  };

  const togglePkg = () => {
    if (pkgModal) {
      setPkgEditData(null);
      setPkgIsEdit(false);
      pkgValidation.resetForm();
    }
    setPkgModal(!pkgModal);
  };

  const handlePkgEdit = (row) => {
    setPkgEditData(row);
    setPkgIsEdit(true);
    setPkgModal(true);
  };

  // ==================== ABONELİK FORM ====================
  const subValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      ID: (subEditData && subEditData.ID) || 0,
      Users_ID: (subEditData && subEditData.Users_ID) || "",
      Premium_Package_ID: (subEditData && subEditData.Premium_Package_ID) || "",
      Start_Date: subEditData ? toInputDate(subEditData.Start_Date) : toInputDate(new Date()),
      End_Date: subEditData ? toInputDate(subEditData.End_Date) : "",
      IsActive:
        subEditData && typeof subEditData.IsActive === "boolean"
          ? subEditData.IsActive
          : true,
    },
    validationSchema: Yup.object({
      Users_ID: Yup.number().typeError("Kullanıcı seçiniz.").required("Kullanıcı zorunludur."),
      Premium_Package_ID: Yup.number()
        .typeError("Paket seçiniz.")
        .required("Paket zorunludur."),
      Start_Date: Yup.string().required("Başlangıç tarihi zorunludur."),
      End_Date: Yup.string().required("Bitiş tarihi zorunludur."),
    }),
    onSubmit: (values) => saveSubscription(values),
  });

  // Paket seçilince bitiş tarihini paketin süresine göre otomatik hesapla
  const handlePackageChange = (e) => {
    const pkgId = e.target.value;
    subValidation.setFieldValue("Premium_Package_ID", pkgId);
    const pkg = packageList.find((p) => String(p.ID) === String(pkgId));
    const startStr = subValidation.values.Start_Date || toInputDate(new Date());
    if (pkg && pkg.Duration_Months) {
      const start = new Date(startStr);
      start.setMonth(start.getMonth() + Number(pkg.Duration_Months));
      subValidation.setFieldValue("End_Date", toInputDate(start));
    }
  };

  const saveSubscription = async (values) => {
    const payload = {
      Users_ID: parseInt(values.Users_ID),
      Premium_Package_ID: parseInt(values.Premium_Package_ID),
      Start_Date: new Date(values.Start_Date).toISOString(),
      End_Date: new Date(values.End_Date).toISOString(),
      IsActive: !!values.IsActive,
    };
    try {
      if (subIsEdit) {
        await axios.patch(SUB_URLS.update(values.ID), payload);
        showToast("Başarılı", "Abonelik güncellendi.", true);
      } else {
        await axios.post(SUB_URLS.add, payload);
        showToast("Başarılı", "Abonelik tanımlandı.", true);
      }
      toggleSub();
      fetchSubscriptions();
    } catch (e) {
      console.error("Abonelik kaydetme hatası:", e);
      showToast("Hata", "İşlem başarısız.", false);
    }
  };

  const toggleSub = () => {
    if (subModal) {
      setSubEditData(null);
      setSubIsEdit(false);
      subValidation.resetForm();
    }
    setSubModal(!subModal);
  };

  const handleSubEdit = (row) => {
    setSubEditData(row);
    setSubIsEdit(true);
    setSubModal(true);
  };

  // Aboneliği hızlı iptal et (IsActive=false)
  const handleRevoke = async (row) => {
    try {
      await axios.patch(SUB_URLS.update(row.ID), { IsActive: false });
      showToast("Başarılı", "Abonelik iptal edildi.", true);
      fetchSubscriptions();
    } catch (e) {
      showToast("Hata", "İptal başarısız.", false);
    }
  };

  // ==================== SİLME ====================
  const handleDeleteClick = (type, row) => {
    setDeleteType(type);
    setDataToDelete(row);
    setDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    if (dataToDelete && dataToDelete.ID) {
      try {
        if (deleteType === "pkg") {
          await axios.delete(PKG_URLS.delete(dataToDelete.ID));
          fetchPackages();
        } else {
          await axios.delete(SUB_URLS.delete(dataToDelete.ID));
          fetchSubscriptions();
        }
        showToast("Başarılı", "Kayıt silindi.", true);
      } catch (e) {
        showToast("Hata", "Silme başarısız.", false);
      }
    }
    setDeleteModal(false);
  };

  // ==================== TABLO KOLONLARI ====================
  const pkgColumns = useMemo(
    () => [
      { Header: "ID", accessor: "ID", Cell: ({ value }) => <span className="text-muted">#{value}</span> },
      {
        Header: "Paket Adı",
        accessor: "Title",
        Cell: ({ value }) => <span className="fw-bold text-primary">{value}</span>,
      },
      {
        Header: "Fiyat",
        accessor: "Price",
        Cell: ({ value }) => <span className="fw-bold">{Number(value || 0).toFixed(2)} ₺</span>,
      },
      {
        Header: "Süre (Ay)",
        accessor: "Duration_Months",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Durum",
        accessor: "IsActive",
        Cell: ({ value }) =>
          value ? (
            <Badge color="success">Aktif</Badge>
          ) : (
            <Badge color="secondary">Pasif</Badge>
          ),
      },
      {
        Header: "İşlemler",
        Cell: (cellProps) => (
          <div className="d-flex gap-3">
            <Link to="#" className="text-success" onClick={() => handlePkgEdit(cellProps.row.original)}>
              <i className="mdi mdi-pencil font-size-18" id="pkgedit" />
              <UncontrolledTooltip placement="top" target="pkgedit">Düzenle</UncontrolledTooltip>
            </Link>
            <Link to="#" className="text-danger" onClick={() => handleDeleteClick("pkg", cellProps.row.original)}>
              <i className="mdi mdi-delete font-size-18" id="pkgdel" />
              <UncontrolledTooltip placement="top" target="pkgdel">Sil</UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    []
  );

  const subColumns = useMemo(
    () => [
      { Header: "ID", accessor: "ID", Cell: ({ value }) => <span className="text-muted">#{value}</span> },
      {
        Header: "Kullanıcı",
        accessor: "Users.Users_NameSurname",
        Cell: ({ row }) => {
          const u = row.original.Users;
          return (
            <div>
              <div className="fw-bold">{u?.Users_NameSurname || `Kullanıcı #${row.original.Users_ID}`}</div>
              <small className="text-muted">{u?.EMail || ""}</small>
            </div>
          );
        },
      },
      {
        Header: "Paket",
        accessor: "Premium_Package.Title",
        Cell: ({ row }) => row.original.Premium_Package?.Title || `#${row.original.Premium_Package_ID}`,
      },
      { Header: "Başlangıç", accessor: "Start_Date", Cell: ({ value }) => fmtDate(value) },
      { Header: "Bitiş", accessor: "End_Date", Cell: ({ value }) => fmtDate(value) },
      {
        Header: "Durum",
        Cell: ({ row }) => {
          const r = row.original;
          const expired = r.End_Date && new Date(r.End_Date) < new Date();
          if (!r.IsActive) return <Badge color="secondary">İptal</Badge>;
          if (expired) return <Badge color="danger">Süresi Doldu</Badge>;
          return <Badge color="success">Aktif</Badge>;
        },
      },
      {
        Header: "İşlemler",
        Cell: (cellProps) => {
          const r = cellProps.row.original;
          return (
            <div className="d-flex gap-3">
              <Link to="#" className="text-success" onClick={() => handleSubEdit(r)}>
                <i className="mdi mdi-pencil font-size-18" id="subedit" />
                <UncontrolledTooltip placement="top" target="subedit">Düzenle</UncontrolledTooltip>
              </Link>
              {r.IsActive && (
                <Link to="#" className="text-warning" onClick={() => handleRevoke(r)}>
                  <i className="mdi mdi-cancel font-size-18" id="subrevoke" />
                  <UncontrolledTooltip placement="top" target="subrevoke">İptal Et</UncontrolledTooltip>
                </Link>
              )}
              <Link to="#" className="text-danger" onClick={() => handleDeleteClick("sub", r)}>
                <i className="mdi mdi-delete font-size-18" id="subdel" />
                <UncontrolledTooltip placement="top" target="subdel">Sil</UncontrolledTooltip>
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={onDeleteConfirm}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Yönetim" breadcrumbItem="Premium & Abonelikler" />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Nav tabs className="nav-tabs-custom mb-4">
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === "packages" })}
                        onClick={() => setActiveTab("packages")}
                      >
                        <i className="mdi mdi-package-variant-closed me-1" /> Paketler
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({ active: activeTab === "subs" })}
                        onClick={() => setActiveTab("subs")}
                      >
                        <i className="mdi mdi-account-star me-1" /> Abonelikler
                      </NavLink>
                    </NavItem>
                  </Nav>

                  {activeTab === "packages" && (
                    <TableContainer
                      columns={pkgColumns}
                      data={packageList}
                      isGlobalFilter={true}
                      isAddAvailable={true}
                      handleOrderClicks={togglePkg}
                      customPageSize={10}
                      className="custom-header-css"
                    />
                  )}

                  {activeTab === "subs" && (
                    <TableContainer
                      columns={subColumns}
                      data={subList}
                      isGlobalFilter={true}
                      isAddAvailable={true}
                      handleOrderClicks={toggleSub}
                      customPageSize={10}
                      className="custom-header-css"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ---------- PAKET MODAL ---------- */}
          <Modal isOpen={pkgModal} toggle={togglePkg} centered>
            <ModalHeader toggle={togglePkg} tag="h4">
              {pkgIsEdit ? "Paketi Düzenle" : "Yeni Premium Paket"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={(e) => { e.preventDefault(); pkgValidation.handleSubmit(); }}>
                <FormGroup className="mb-3">
                  <Label>Paket Adı</Label>
                  <Input
                    name="Title"
                    type="text"
                    placeholder="Örn: Aylık Premium"
                    onChange={pkgValidation.handleChange}
                    onBlur={pkgValidation.handleBlur}
                    value={pkgValidation.values.Title}
                    invalid={!!(pkgValidation.touched.Title && pkgValidation.errors.Title)}
                  />
                  <FormFeedback>{pkgValidation.errors.Title}</FormFeedback>
                </FormGroup>

                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Fiyat (₺)</Label>
                      <Input
                        name="Price"
                        type="number"
                        step="0.01"
                        onChange={pkgValidation.handleChange}
                        value={pkgValidation.values.Price}
                        invalid={!!(pkgValidation.touched.Price && pkgValidation.errors.Price)}
                      />
                      <FormFeedback>{pkgValidation.errors.Price}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Süre (Ay)</Label>
                      <Input
                        name="Duration_Months"
                        type="number"
                        onChange={pkgValidation.handleChange}
                        value={pkgValidation.values.Duration_Months}
                        invalid={!!(pkgValidation.touched.Duration_Months && pkgValidation.errors.Duration_Months)}
                      />
                      <FormFeedback>{pkgValidation.errors.Duration_Months}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup className="mb-3 form-check">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="pkgIsActive"
                    name="IsActive"
                    checked={pkgValidation.values.IsActive}
                    onChange={pkgValidation.handleChange}
                  />
                  <Label className="form-check-label" for="pkgIsActive">
                    Aktif (uygulamada satışta)
                  </Label>
                </FormGroup>

                <div className="d-flex justify-content-end mt-4 gap-2">
                  <Button color="secondary" onClick={togglePkg}>İptal</Button>
                  <Button type="submit" color="success">
                    {pkgIsEdit ? "Kaydet" : "Ekle"}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>

          {/* ---------- ABONELİK MODAL ---------- */}
          <Modal isOpen={subModal} toggle={toggleSub} centered>
            <ModalHeader toggle={toggleSub} tag="h4">
              {subIsEdit ? "Aboneliği Düzenle" : "Kullanıcıya Premium Tanımla"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={(e) => { e.preventDefault(); subValidation.handleSubmit(); }}>
                <FormGroup className="mb-3">
                  <Label>Kullanıcı</Label>
                  <Input
                    type="select"
                    name="Users_ID"
                    onChange={subValidation.handleChange}
                    onBlur={subValidation.handleBlur}
                    value={subValidation.values.Users_ID}
                    invalid={!!(subValidation.touched.Users_ID && subValidation.errors.Users_ID)}
                  >
                    <option value="">— Kullanıcı seçiniz —</option>
                    {usersList.map((u) => (
                      <option key={u.ID} value={u.ID}>
                        {u.Users_NameSurname || `Kullanıcı #${u.ID}`} {u.EMail ? `(${u.EMail})` : ""}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{subValidation.errors.Users_ID}</FormFeedback>
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label>Paket</Label>
                  <Input
                    type="select"
                    name="Premium_Package_ID"
                    onChange={handlePackageChange}
                    onBlur={subValidation.handleBlur}
                    value={subValidation.values.Premium_Package_ID}
                    invalid={!!(subValidation.touched.Premium_Package_ID && subValidation.errors.Premium_Package_ID)}
                  >
                    <option value="">— Paket seçiniz —</option>
                    {packageList.map((p) => (
                      <option key={p.ID} value={p.ID}>
                        {p.Title} — {Number(p.Price || 0).toFixed(2)} ₺ / {p.Duration_Months || 1} ay
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{subValidation.errors.Premium_Package_ID}</FormFeedback>
                </FormGroup>

                <Row>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Başlangıç</Label>
                      <Input
                        name="Start_Date"
                        type="date"
                        onChange={subValidation.handleChange}
                        value={subValidation.values.Start_Date}
                        invalid={!!(subValidation.touched.Start_Date && subValidation.errors.Start_Date)}
                      />
                      <FormFeedback>{subValidation.errors.Start_Date}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-3">
                      <Label>Bitiş</Label>
                      <Input
                        name="End_Date"
                        type="date"
                        onChange={subValidation.handleChange}
                        value={subValidation.values.End_Date}
                        invalid={!!(subValidation.touched.End_Date && subValidation.errors.End_Date)}
                      />
                      <FormFeedback>{subValidation.errors.End_Date}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup className="mb-3 form-check">
                  <Input
                    type="checkbox"
                    className="form-check-input"
                    id="subIsActive"
                    name="IsActive"
                    checked={subValidation.values.IsActive}
                    onChange={subValidation.handleChange}
                  />
                  <Label className="form-check-label" for="subIsActive">
                    Aktif
                  </Label>
                </FormGroup>

                <div className="d-flex justify-content-end mt-4 gap-2">
                  <Button color="secondary" onClick={toggleSub}>İptal</Button>
                  <Button type="submit" color="success">
                    {subIsEdit ? "Kaydet" : "Tanımla"}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(PremiumManager);
